/**
 * Pure helpers for /billing/kitchen (Plan 20).
 *
 * Framework-free so the unit suite at
 * `apps/ordo/client/test/components/kitchen-payouts-helpers.test.ts`
 * can exercise the formatters + grouping logic without DOM.
 *
 * Mirrors the server-side helpers in
 * `apps/ordo/server/src/managers/kitchen-payouts.ts` (kindLabel,
 * payoutStatusLabel, payoutStatusTone, etc.) so the client + server
 * stay in lockstep. The duplication is intentional: client bundles
 * shouldn't pull in the server tree.
 */

import type { KitchenPayoutLineRow, KitchenPayoutRow, PayoutStatus, VendorPayoutLineKind } from '@/api/billing-kitchen'

/// User-visible payout kinds, ordered for stable rendering.
/// Clawbacks, adjustments, and rate corrections must never be displayed.
export const VENDOR_PAYOUT_LINE_KINDS_ORDERED: ReadonlyArray<VendorPayoutLineKind> = [
  'ITEM',
  'CARVE_OUT',
  'VENDOR_SERVICE_FEE',
]

/**
 * Format a (possibly negative) cents amount as a localized currency
 * string. Currency code comes from the page payload (one currency
 * per kitchen in v1; mixed currencies are blocked at cycle-generator
 * time).
 *
 * Negative values render with a leading minus per the en-US
 * convention: `formatCents(-1200, 'USD') === '-$12.00'`.
 */
export function formatCents(amountCents: number, currency: string): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    signDisplay: amountCents < 0 ? 'always' : 'auto',
  })
  return formatter.format(amountCents / 100)
}

/**
 * Human-readable label for each `VendorPayoutLineKind`. Plan 20's
 * mockup spec calls out chef-facing copy: "Kitchen pay" reads better
 * to a chef than `ITEM`.
 */
export function kindLabel(kind: VendorPayoutLineKind): string {
  switch (kind) {
    case 'ITEM':
      return 'Kitchen pay'
    case 'CARVE_OUT':
      return 'Carve-out pay'
    case 'VENDOR_SERVICE_FEE':
      return 'Service fees'
    case 'ADJUSTMENT':
      return 'Adjustments'
    case 'RATE_CORRECTION':
      // A re-priced meal, never a clawback — see the schema enum.
      return 'Rate corrections'
    case 'CLAWBACK':
      return ''
  }
}

/**
 * Human-readable label for each `PayoutStatus`. The PayoutStatusChip
 * reads this for its label.
 */
export function payoutStatusLabel(status: PayoutStatus): string {
  switch (status) {
    case 'PENDING':
      return 'Pending'
    case 'BILLED':
      return 'Processing'
    case 'SENT':
      return 'Sent'
    case 'PAID':
      return 'Paid'
    case 'FAILED':
      return 'Failed'
    case 'HELD':
      return 'Held'
  }
}

/**
 * Badge variant for each `PayoutStatus` per Plan 20 acceptance #6:
 *   PENDING gray  → 'secondary'
 *   SENT    blue  → 'info'
 *   PAID    green → 'success'
 *   FAILED  red   → 'destructive'
 *   HELD    yellow→ 'warning'
 */
export type PayoutStatusTone = 'secondary' | 'info' | 'success' | 'destructive' | 'warning'

export function payoutStatusTone(status: PayoutStatus): PayoutStatusTone {
  switch (status) {
    case 'PENDING':
      return 'secondary'
    case 'BILLED':
      return 'info'
    case 'SENT':
      return 'info'
    case 'PAID':
      return 'success'
    case 'FAILED':
      return 'destructive'
    case 'HELD':
      return 'warning'
  }
}

/**
 * Group lines by their `kind`. Returns a `Partial<Record<kind, lines>>`
 * shape so callers can iterate `VENDOR_PAYOUT_LINE_KINDS_ORDERED` and
 * skip empty buckets.
 */
export function groupLinesByKind(
  lines: readonly KitchenPayoutLineRow[],
): Partial<Record<VendorPayoutLineKind, KitchenPayoutLineRow[]>> {
  const out: Partial<Record<VendorPayoutLineKind, KitchenPayoutLineRow[]>> = {}
  for (const line of lines) {
    if (!VENDOR_PAYOUT_LINE_KINDS_ORDERED.includes(line.kind)) {
      continue
    }
    const bucket = out[line.kind] ?? (out[line.kind] = [])
    bucket.push(line)
  }
  return out
}

/**
 * Sum a set of payout lines by their `amountCents`. Negative values
 * (CLAWBACK) are included as-is so the per-kind subtotal matches the
 * envelope total.
 */
export function sumLineAmounts(lines: readonly KitchenPayoutLineRow[]): number {
  let total = 0
  for (const line of lines) {
    total += line.amountCents
  }
  return total
}

/**
 * Format a YYYY-MM-DD `cycleDate` for display. Plan 20's card uses
 * the long format ("Friday, May 29"); the table uses the short
 * format ("May 29, 2026"). Returns the raw input when the date can't
 * be parsed (defensive — the server always serializes the canonical
 * shape).
 */
export function formatCycleDateLong(cycleDate: string): string {
  const d = parseCycleDate(cycleDate)
  if (!d) {
    return cycleDate
  }
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export function formatCycleDateShort(cycleDate: string): string {
  const d = parseCycleDate(cycleDate)
  if (!d) {
    return cycleDate
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function parseCycleDate(cycleDate: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(cycleDate)
  if (!m) {
    return null
  }
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (mo < 1 || mo > 12 || d < 1 || d > 31) {
    return null
  }
  return new Date(Date.UTC(y, mo - 1, d))
}

/**
 * True when the envelope's total is strictly negative — Plan 20 edge
 * case: "Payout with all-CLAWBACK lines" renders in destructive red
 * with a "(Applied to next cycle)" hint.
 */
export function isNegativePayout(row: Pick<KitchenPayoutRow, 'totalCents'>): boolean {
  return row.totalCents < 0
}

/**
 * Compare two payout rows for desc-by-cycleDate-then-createdAt sort.
 * Used client-side when the page wants to re-sort after a filter.
 */
export function comparePayoutsDesc(a: KitchenPayoutRow, b: KitchenPayoutRow): number {
  if (a.cycleDate < b.cycleDate) {
    return 1
  }
  if (a.cycleDate > b.cycleDate) {
    return -1
  }
  if (a.createdAt < b.createdAt) {
    return 1
  }
  if (a.createdAt > b.createdAt) {
    return -1
  }
  return 0
}

// ---------------------------------------------------------------------------
// Item folding — what a chef actually reads
// ---------------------------------------------------------------------------

/**
 * One item, as many servings as the payout covered, on one delivery date at
 * one campus.
 *
 * A payout pays PER ORDER ITEM, so the same menu item bought by 40 students is
 * 40 lines — 830 of them on one real September envelope, 6,221 on the largest.
 * Folding turns that into 82 and 401 rows respectively: a list a chef can
 * review rather than a wall they scroll past.
 *
 * Deliberately re-created here rather than shared with `apps/customers`'
 * equivalent: the fold is presentation, the two apps own their own wire types,
 * and the shared piece that matters (the rendering) is `@ordo/ui`'s
 * `PayoutLineGroups`.
 */
export interface PayoutItemRow {
  key: string
  /** Frozen submission-snapshot name; falls back to the line's own label. */
  itemName: string
  schoolName: string | null
  /**
   * `YYYY-MM-DD` the food was SERVED, null when unresolved.
   *
   * Part of the fold key, not decoration. One delivery can carry more than one
   * service day, and folding those together is what hid a whole day of a chef's
   * pay behind the previous day's delivery row (PROD-1359).
   */
  serviceDate: string | null
  /** Null when no folded line resolved a quantity — never coerced to 0. */
  quantity: number | null
  amountCents: number
  /** How many payout lines folded into this row. */
  lineCount: number
  isDryRun: boolean
}

/** Item rows for one delivery date. `deliveryDate` is null when unresolved. */
export interface PayoutItemDateGroup {
  deliveryDate: string | null
  rows: PayoutItemRow[]
  quantity: number | null
  amountCents: number
}

/**
 * The items a payout covers, grouped by DELIVERY DATE — the day the food
 * reached the school, which is what a chef reconciles against, not the service
 * date and not the cycle date.
 *
 * Only visible payout kinds with a source order item take part. Corrections
 * and adjustments stay hidden even when they reference a delivered item.
 *
 * `isDryRun` is part of the fold key. A test cook and live service for the same
 * item on the same day are different facts, and merging them would erase the
 * only marker that says so.
 *
 * Dates sort ascending (the cycle read as a calendar); an unresolved date sorts
 * last, since it is the exception and never the thing being scanned for.
 */
export function groupItemLinesByDelivery(lines: readonly KitchenPayoutLineRow[]): PayoutItemDateGroup[] {
  const byDate = new Map<string, Map<string, PayoutItemRow>>()
  for (const line of lines) {
    if ((line.kind !== 'ITEM' && line.kind !== 'CARVE_OUT') || !line.sourceOrderItemId) {
      continue
    }
    const dateKey = line.deliveryDate ?? ''
    const itemName = line.itemName ?? line.description
    const rowKey = `${line.schoolName ?? ''}|${itemName}|${line.isDryRun ? 'dry' : 'live'}|${line.serviceDate ?? ''}`
    const rows = byDate.get(dateKey) ?? new Map<string, PayoutItemRow>()
    const existing = rows.get(rowKey)
    if (existing) {
      existing.quantity = line.quantity === null ? existing.quantity : (existing.quantity ?? 0) + line.quantity
      existing.amountCents += line.amountCents
      existing.lineCount += 1
    } else {
      rows.set(rowKey, {
        key: `${dateKey}|${rowKey}`,
        itemName,
        schoolName: line.schoolName,
        serviceDate: line.serviceDate,
        quantity: line.quantity,
        amountCents: line.amountCents,
        lineCount: 1,
        isDryRun: line.isDryRun,
      })
    }
    byDate.set(dateKey, rows)
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
    .map(([dateKey, rows]) => {
      const groupRows = [...rows.values()].sort(
        (a, b) =>
          (a.serviceDate ?? '').localeCompare(b.serviceDate ?? '') ||
          (a.schoolName ?? '').localeCompare(b.schoolName ?? '') ||
          a.itemName.localeCompare(b.itemName),
      )
      const withQuantity = groupRows.filter((row) => row.quantity !== null)
      return {
        deliveryDate: dateKey === '' ? null : dateKey,
        rows: groupRows,
        quantity: withQuantity.length === 0 ? null : withQuantity.reduce((sum, row) => sum + (row.quantity ?? 0), 0),
        amountCents: groupRows.reduce((sum, row) => sum + row.amountCents, 0),
      }
    })
}

/** Identical line descriptions within one kind, folded into one row. */
export interface PayoutKindEntry {
  description: string
  amountCents: number
  /** How many `VendorPayoutLine` rows folded in. */
  lineCount: number
}

/**
 * Fold a kind's lines by their description, so the by-kind breakdown stays a
 * summary instead of repeating the 830 rows the item view already itemises.
 * Adjustments and clawbacks carry their own text and stay individually visible.
 */
export function foldLinesByDescription(lines: readonly KitchenPayoutLineRow[]): PayoutKindEntry[] {
  const entries = new Map<string, PayoutKindEntry>()
  for (const line of lines) {
    const existing = entries.get(line.description)
    if (existing) {
      existing.amountCents += line.amountCents
      existing.lineCount += 1
    } else {
      entries.set(line.description, { description: line.description, amountCents: line.amountCents, lineCount: 1 })
    }
  }
  return [...entries.values()]
}

/**
 * The campus + served-day caption under an item row.
 *
 * The served date shows only when it differs from the group's delivery date —
 * which is precisely when the group is hiding something. A delivery that
 * carried two service days otherwise renders as one undifferentiated block and
 * the extra day is invisible (PROD-1359). When the dates agree, the group
 * header already says it and repeating it on every row is noise.
 */
export function payoutItemRowDetail(row: PayoutItemRow, groupDeliveryDate: string | null): string | null {
  const served = row.serviceDate && row.serviceDate !== groupDeliveryDate ? `Served ${row.serviceDate}` : null
  return [row.schoolName, served].filter((part): part is string => Boolean(part)).join(' · ') || null
}

/**
 * `2026-09-03` → `Thu, Sep 3`. Parsed as UTC parts, never `new Date(string)` in
 * local time — that renders the previous day west of Greenwich, which on a
 * delivery-date header is simply a wrong fact.
 */
export function formatDeliveryDate(date: string | null): string {
  if (!date) {
    return 'Delivery date unresolved'
  }
  const parsed = new Date(`${date}T00:00:00.000Z`)
  if (Number.isNaN(parsed.getTime())) {
    return date
  }
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(parsed)
}

/**
 * The delivery-date window an envelope settles, as a chef-readable range.
 *
 * `cycleDate` alone is only the window's upper bound, so a chef shown the cycle
 * date cannot tell which service days they are being paid for. Returns null
 * when the envelope carries no window (pre-dates the stamp) — callers omit the
 * line entirely rather than printing a half-range.
 */
export function formatPayoutPeriod(periodStart: string | null, periodEnd: string | null): string | null {
  if (!periodStart || !periodEnd) {
    return null
  }
  if (periodStart === periodEnd) {
    return formatDeliveryDate(periodStart)
  }
  return `${formatDeliveryDate(periodStart)} – ${formatDeliveryDate(periodEnd)}`
}
